import { useState, useEffect, useCallback } from 'react';
import { mastersApi } from '@/features/masters/api';
import { CompanyVehicle, CompanyVehicleInsert, CompanyVehicleUpdate } from '@/features/masters/types';

/**
 * 社用車データを管理するフック (FastAPI経由)
 */
export function useCompanyVehicles() {
  const [companyVehicles, setCompanyVehicles] = useState<CompanyVehicle[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // データを取得する関数
  const fetchCompanyVehicles = useCallback(async () => {
    try {
      setIsLoading(true);
      setError(null);
      const data = await mastersApi.companyVehicles.getAll();
      setCompanyVehicles(data);
    } catch (err: unknown) {
      console.error('Failed to fetch company vehicles:', err);
      const errorMessage = err instanceof Error && 'response' in err && err.response && typeof err.response === 'object' && 'data' in err.response && err.response.data && typeof err.response.data === 'object' && 'message' in err.response.data && typeof err.response.data.message === 'string'
        ? err.response.data.message
        : '社用車データの取得に失敗しました';
      setError(errorMessage);
    } finally {
      setIsLoading(false);
    }
  }, []);

  // 初回読み込み
  useEffect(() => {
    fetchCompanyVehicles();
  }, [fetchCompanyVehicles]);

  // 社用車作成
  const createEntity = async (data: CompanyVehicleInsert) => {
    try {
      const result = await mastersApi.companyVehicles.create(data);
      await fetchCompanyVehicles(); // データを再取得
      return result;
    } catch (error) {
      console.error('Error creating company vehicle:', error);
      throw error;
    }
  };

  // 社用車更新
  const updateEntity = async (id: string, data: CompanyVehicleUpdate) => {
    try {
      const result = await mastersApi.companyVehicles.update(id, data);
      await fetchCompanyVehicles(); // データを再取得
      return result;
    } catch (error) {
      console.error('Error updating company vehicle:', error);
      throw error;
    }
  };

  // 社用車削除
  const deleteEntity = async (id: string) => {
    try {
      await mastersApi.companyVehicles.delete(id);
      await fetchCompanyVehicles(); // データを再取得
      return true;
    } catch (error) {
      console.error('Error deleting company vehicle:', error);
      throw error;
    }
  };

  return {
    data: companyVehicles,
    items: companyVehicles,
    isLoading,
    error,
    // 互換性のための追加プロパティ
    refreshData: fetchCompanyVehicles,
    createEntity,
    updateEntity,
    deleteEntity,
  };
}
